import { useState, useRef, useEffect } from 'react';
import { Mic, Send, Square, Plus, X, Settings2, Plug, ChevronRight, Loader2, Check } from 'lucide-react';
import { googleDriveService } from '../services/google-drive-service';
import { googleCalendarService } from '../services/google-calendar-service';

interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (files?: File[], forcedTools?: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onFilesChange?: (files: File[]) => void;
  externalFiles?: File[];
  userId?: string; // ID do usuário para integrações
  onToolsChange?: (tools: string[]) => void; // Callback para ferramentas disponíveis
  onForcedToolsChange?: (tools: string[]) => void; // Callback para ferramentas forçadas pelo usuário
  enabledTools?: string[]; // Ferramentas já ativas/disponíveis
}

export function MessageInput({
  value,
  onChange,
  onSend,
  placeholder = 'Insira seu comando para a SPACE',
  disabled = false,
  className = '',
  onFilesChange,
  externalFiles,
  userId,
  onToolsChange,
  onForcedToolsChange,
  enabledTools = []
}: MessageInputProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevels, setAudioLevels] = useState<number[]>([0, 0, 0, 0, 0]);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [isLoadingDrive, setIsLoadingDrive] = useState(false);
  
  // Estados para ferramentas
  const [activeTools, setActiveTools] = useState<string[]>(enabledTools); // Ferramentas disponíveis (do agente)
  const [forcedTools, setForcedTools] = useState<string[]>([]); // Ferramentas forçadas pelo usuário no chat
  const [toolConnections, setToolConnections] = useState<Record<string, boolean>>({});
  const [connectingTool, setConnectingTool] = useState<string | null>(null);
  const toolsMenuRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const animationFrameRef = useRef<number>();
  const timerIntervalRef = useRef<NodeJS.Timeout>();
  const audioContextRef = useRef<AudioContext>();
  const analyserRef = useRef<AnalyserNode>();

  // Verificar status de conexão das ferramentas
  useEffect(() => {
    const checkConnections = async () => {
      if (!userId) return;
      
      try {
        const calendarConnected = await googleCalendarService.isConnected(userId);
        const driveConnected = await googleDriveService.isConnected(userId);
        
        setToolConnections({
          google_calendar: calendarConnected,
          google_drive: driveConnected
        });
      } catch (error) {
        console.error('Erro ao verificar conexões:', error);
      }
    };
    
    checkConnections();
  }, [userId]);

  // Sincronizar ferramentas ativas com prop externa
  useEffect(() => {
    setActiveTools(enabledTools);
  }, [enabledTools]);

  // Fechar menu de ferramentas ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target as Node)) {
        setShowToolsMenu(false);
      }
    };

    if (showToolsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showToolsMenu]);

  // Toggle de ferramenta forçada pelo usuário
  const handleToggleForcedTool = async (toolId: string) => {
    const isConnected = toolConnections[toolId] || toolId === 'web_search'; // Web search não precisa conexão
    const isForced = forcedTools.includes(toolId);
    
    if (!isConnected && toolId !== 'web_search') {
      // Se não está conectada, iniciar conexão
      setConnectingTool(toolId);
      try {
        if (toolId === 'google_calendar') {
          await googleCalendarService.getAccessToken(userId!);
          setToolConnections(prev => ({ ...prev, google_calendar: true }));
        } else if (toolId === 'google_drive') {
          await googleDriveService.getAccessToken(userId!);
          setToolConnections(prev => ({ ...prev, google_drive: true }));
        }
        // Após conectar, forçar a ferramenta
        const newForcedTools = [...forcedTools, toolId];
        setForcedTools(newForcedTools);
        onForcedToolsChange?.(newForcedTools);
      } catch (error) {
        console.error(`Erro ao conectar ${toolId}:`, error);
      } finally {
        setConnectingTool(null);
      }
    } else {
      // Se já está conectada (ou não precisa), toggle na lista de forçadas
      let newForcedTools: string[];
      if (isForced) {
        newForcedTools = forcedTools.filter(t => t !== toolId);
      } else {
        newForcedTools = [...forcedTools, toolId];
      }
      setForcedTools(newForcedTools);
      onForcedToolsChange?.(newForcedTools);
    }
  };

  // Sincronizar arquivos externos
  useEffect(() => {
    if (externalFiles) {
      if (externalFiles.length > 0) {
        console.log('📎 Recebendo arquivos externos:', externalFiles.map(f => f.name));
        const existingNames = attachedFiles.map(f => f.name);
        const newFiles = externalFiles.filter(f => !existingNames.includes(f.name));
        if (newFiles.length > 0) {
          const updatedFiles = [...attachedFiles, ...newFiles];
          setAttachedFiles(updatedFiles);
          if (onFilesChange) {
            onFilesChange(updatedFiles);
          }
        }
      } else {
        console.log('🧹 Limpando arquivos externos');
        setAttachedFiles([]);
      }
    }
  }, [externalFiles]);

  // Auto resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      
      if (!value || value.trim() === '') {
        textarea.style.height = 'auto';
        textarea.style.overflowY = 'hidden';
        return;
      }
      
      textarea.style.height = 'auto';
      
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 24;
      const maxLines = 4;
      const maxHeight = lineHeight * maxLines;
      
      const scrollHeight = textarea.scrollHeight;
      if (scrollHeight <= maxHeight) {
        textarea.style.height = `${scrollHeight}px`;
        textarea.style.overflowY = 'hidden';
      } else {
        textarea.style.height = `${maxHeight}px`;
        textarea.style.overflowY = 'auto';
      }
    }
  }, [value]);

  const handleSendInternal = () => {
    console.log('🔄 handleSendInternal chamado:', {
      value: value.trim(),
      attachedFilesCount: attachedFiles.length,
      forcedTools,
      disabled
    });
    
    if ((value.trim() || attachedFiles.length > 0) && !disabled) {
      console.log('✅ Enviando:', {
        texto: value.trim(),
        arquivos: attachedFiles.map(f => f.name),
        forcedTools
      });
      onSend(
        attachedFiles.length > 0 ? attachedFiles : undefined,
        forcedTools.length > 0 ? forcedTools : undefined
      );
      clearFiles();
      // Limpar ferramentas forçadas após enviar (serão usadas apenas nesta mensagem)
      setForcedTools([]);
      onForcedToolsChange?.([]);
    } else {
      console.log('❌ Envio bloqueado - condições não atendidas');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Só envia se não estiver desabilitado (processando)
      if (!disabled) {
      handleSendInternal();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const files: File[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          console.log('📎 Arquivo colado via Ctrl+V:', file.name, file.type);
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const visualizeAudio = (stream: MediaStream) => {
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);
    
    analyser.fftSize = 32;
    source.connect(analyser);
    
    audioContextRef.current = audioContext;
    analyserRef.current = analyser;
    
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const animate = () => {
      analyser.getByteFrequencyData(dataArray);
      
      const levels = [
        dataArray[1] / 255,
        dataArray[3] / 255,
        dataArray[5] / 255,
        dataArray[7] / 255,
        dataArray[9] / 255,
      ];
      
      setAudioLevels(levels);
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animate();
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      visualizeAudio(stream);

      setRecordingTime(0);
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
        
        stream.getTracks().forEach(track => track.stop());
        
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        
        setRecordingTime(0);
        setAudioLevels([0, 0, 0, 0, 0]);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Erro ao acessar microfone:', error);
      alert('Não foi possível acessar o microfone. Verifique as permissões.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      addFiles(files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addFiles = (files: File[]) => {
    console.log('➕ Adicionando arquivos:', files.map(f => f.name));
    const newFiles = [...attachedFiles, ...files];
    console.log('📋 Total de arquivos após adicionar:', newFiles.length);
    setAttachedFiles(newFiles);
    if (onFilesChange) {
      onFilesChange(newFiles);
    }
  };

  const removeFile = (index: number) => {
    const newFiles = attachedFiles.filter((_, i) => i !== index);
    setAttachedFiles(newFiles);
    if (onFilesChange) {
      onFilesChange(newFiles);
    }
  };

  const clearFiles = () => {
    setAttachedFiles([]);
    if (onFilesChange) {
      onFilesChange([]);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (file: File): string => {
    const type = file.type;
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.startsWith('audio/')) return '🎵';
    if (type.includes('pdf')) return '📄';
    if (type.includes('word') || type.includes('document')) return '📝';
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
    if (type.includes('powerpoint') || type.includes('presentation')) return '📽️';
    if (type.includes('zip') || type.includes('rar') || type.includes('compressed')) return '📦';
    return '📎';
  };

  const transcribeAudio = async (audioBlob: Blob) => {
    setIsTranscribing(true);
    
    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result as string;
        
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        
        if (!supabaseUrl || !supabaseAnonKey) {
          console.error('Supabase não configurado');
          alert('Transcrição de áudio não está configurada. Configure o Supabase.');
          setIsTranscribing(false);
          return;
        }

        const audioFile = await fetch(base64Audio)
          .then(res => res.blob())
          .then(blob => new File([blob], 'audio.webm', { type: 'audio/webm' }));

        const formData = new FormData();
        formData.append('file', audioFile);
        formData.append('model', 'whisper-1');
        formData.append('language', 'pt');

        console.log('🎤 Enviando áudio para transcrição...');

        const response = await fetch(`${supabaseUrl}/functions/v1/whisper-transcribe`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Erro na transcrição:', errorData);
          throw new Error(errorData.error || 'Erro ao transcrever áudio');
        }

        const data = await response.json();
        const transcription = data.text;

        console.log('✅ Transcrição concluída:', transcription);

        const newValue = value ? `${value} ${transcription}` : transcription;
        onChange(newValue);
      };
    } catch (error) {
      console.error('Erro ao transcrever áudio:', error);
      alert('Erro ao transcrever áudio. Tente novamente.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const hasContent = value.trim() || attachedFiles.length > 0;

  return (
    <div className={`flex flex-col space-y-3 ${className}`}>
      {/* Preview de Arquivos Anexados */}
      {attachedFiles.length > 0 && (
        <div className="flex flex-wrap gap-2 px-1">
          {attachedFiles.map((file, index) => (
            <div
              key={index}
              className="flex items-center space-x-2 bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm shadow-sm"
            >
              <span className="text-lg">{getFileIcon(file)}</span>
              <div className="flex-1 min-w-0">
                <p className="text-gray-800 truncate max-w-[150px]">{file.name}</p>
                <p className="text-gray-500 text-xs">{formatFileSize(file.size)}</p>
              </div>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input de Arquivo Oculto */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.zip,.rar"
      />

      {/* Container Principal - Design Gemini Style */}
      <div className="bg-[#f0f0f0] rounded-2xl border border-[#e0e0e0]">
        {!isRecording ? (
          <>
            {/* Área do Textarea */}
            <div className="px-4 pt-4 pb-2">
              <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyPress={handleKeyPress}
                onPaste={handlePaste}
                placeholder={isTranscribing ? 'Transcrevendo áudio...' : placeholder}
                disabled={isRecording || isTranscribing}
                rows={1}
                className="w-full bg-transparent text-gray-800 placeholder-gray-500 resize-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed text-base"
                style={{ minHeight: '24px' }}
              />
            </div>
            
            {/* Barra de Ações */}
            <div className="flex items-center justify-between px-3 pb-3">
              {/* Lado Esquerdo - Botão Anexar e Ferramentas */}
              <div className="flex items-center gap-1">
                {/* Botão Anexar */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
                  disabled={isTranscribing || isRecording}
                  className="p-2 rounded-full hover:bg-[#e0e0e0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Adicionar anexo"
                >
                  <Plus className="w-5 h-5 text-gray-600" />
                </button>

                {/* Botão Ferramentas */}
                <div className="relative" ref={toolsMenuRef} style={{ zIndex: showToolsMenu ? 9999 : 'auto' }}>
                  <button
                    type="button"
                    onClick={() => setShowToolsMenu(!showToolsMenu)}
                    disabled={isTranscribing || isRecording}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      showToolsMenu ? 'bg-[#e0e0e0]' : 'hover:bg-[#e0e0e0]'
                    } ${forcedTools.length > 0 ? 'bg-purple-50 hover:bg-purple-100' : ''}`}
                    title="Ferramentas"
                  >
                    <Settings2 className={`w-4 h-4 ${forcedTools.length > 0 ? 'text-purple-600' : 'text-gray-600'}`} />
                    <span className={`text-sm ${forcedTools.length > 0 ? 'text-purple-600' : 'text-gray-600'}`}>
                      Ferramentas
                      {forcedTools.length > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 text-xs bg-purple-500 text-white rounded-full">
                          {forcedTools.length}
                        </span>
                      )}
                    </span>
            </button>

                  {/* Menu de Ferramentas */}
                  {showToolsMenu && (
                    <div className="absolute bottom-full left-0 mb-2 w-80 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden" style={{ zIndex: 9999 }}>
                      <div className="p-2">
                        <div className="px-3 py-2">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Usar nesta mensagem</p>
                          <p className="text-xs text-gray-400 mt-0.5">Ative para forçar o uso da ferramenta</p>
                        </div>
                        
                        {/* Google Calendar */}
                        <div className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                          forcedTools.includes('google_calendar') ? 'bg-purple-50' : 'hover:bg-gray-50'
                        }`}>
                          <div className="flex items-center gap-3">
                            <img src="/google-calendar-logo.png" alt="Google Calendar" className="w-5 h-5 object-contain" />
                            <div>
                              <span className="text-sm text-gray-700 font-medium">Google Calendar</span>
                              <p className="text-xs text-gray-400">
                                {forcedTools.includes('google_calendar') 
                                  ? '✓ Será usado obrigatoriamente' 
                                  : toolConnections.google_calendar ? 'Disponível' : 'Conectar'}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleToggleForcedTool('google_calendar')}
                            disabled={connectingTool === 'google_calendar'}
                            className={`relative w-10 h-5 rounded-full transition-colors ${
                              forcedTools.includes('google_calendar')
                                ? 'bg-purple-500'
                                : 'bg-gray-300'
                            }`}
                          >
                            {connectingTool === 'google_calendar' ? (
                              <Loader2 className="w-4 h-4 animate-spin absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white" />
                            ) : (
                              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                forcedTools.includes('google_calendar') ? 'translate-x-5' : 'translate-x-0.5'
                              }`}>
                                {forcedTools.includes('google_calendar') && (
                                  <Check className="w-3 h-3 text-purple-500 m-0.5" />
                                )}
                              </div>
                            )}
                          </button>
                        </div>

                        {/* Google Drive - Importar Arquivo */}
                        <div className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex items-center gap-3">
                            <img src="/google-drive-logo.png" alt="Google Drive" className="w-5 h-5 object-contain" />
                            <div>
                              <span className="text-sm text-gray-700 font-medium">Google Drive</span>
                              <p className="text-xs text-gray-400">Importar arquivos</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              setIsLoadingDrive(true);
                              try {
                                if (!googleDriveService.isConfigured()) {
                                  alert('Google Drive não configurado.');
                                  return;
                                }
                                if (!userId) {
                                  alert('Usuário não identificado.');
                                  return;
                                }
                                const files = await googleDriveService.selectAndDownloadFiles(userId);
                                if (files.length > 0) {
                                  addFiles(files);
                                  setShowToolsMenu(false);
                                }
                              } catch (error: any) {
                                console.error('Erro ao acessar Google Drive:', error);
                              } finally {
                                setIsLoadingDrive(false);
                              }
                            }}
                            disabled={isLoadingDrive}
                            className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                          >
                            {isLoadingDrive ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Importar'
                            )}
                          </button>
                        </div>

                        {/* Web Search */}
                        <div className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                          forcedTools.includes('web_search') ? 'bg-purple-50' : 'hover:bg-gray-50'
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className="w-5 h-5 bg-blue-100 rounded flex items-center justify-center">
                              <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                            </div>
                            <div>
                              <span className="text-sm text-gray-700 font-medium">Busca na Web</span>
                              <p className="text-xs text-gray-400">
                                {forcedTools.includes('web_search') 
                                  ? '✓ Será usado obrigatoriamente' 
                                  : 'Pesquisar na internet'}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleToggleForcedTool('web_search')}
                            className={`relative w-10 h-5 rounded-full transition-colors ${
                              forcedTools.includes('web_search')
                                ? 'bg-purple-500'
                                : 'bg-gray-300'
                            }`}
                          >
                            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                              forcedTools.includes('web_search') ? 'translate-x-5' : 'translate-x-0.5'
                            }`}>
                              {forcedTools.includes('web_search') && (
                                <Check className="w-3 h-3 text-purple-500 m-0.5" />
                              )}
                            </div>
                          </button>
                        </div>

                        <div className="border-t border-gray-100 my-2"></div>
                        
                        <p className="text-xs text-gray-400 uppercase tracking-wider px-3 py-2 font-medium">Integrações</p>

                        {/* Adicionar via MCP */}
                        <button
                          type="button"
                          onClick={() => {
                            console.log('Adicionar MCP clicado');
                            setShowToolsMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <Plug className="w-5 h-5 text-purple-600" />
                          <span className="text-sm text-gray-700">Adicionar via MCP</span>
                          <Plus className="w-4 h-4 text-gray-400 ml-auto" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Lado Direito - Botão de Áudio ou Enviar */}
              <div className="flex items-center gap-2">
                {isTranscribing ? (
                  <div className="p-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-blue-500"></div>
                  </div>
                ) : disabled ? (
                  /* Processando - mostra spinner */
                  <div className="p-2 rounded-full bg-gray-200">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-400 border-t-gray-700"></div>
                  </div>
                ) : hasContent ? (
                  <button
                type="button"
                onClick={handleSendInternal}
                    className="p-2 rounded-full bg-gray-800 hover:bg-gray-900 transition-colors"
                    title="Enviar mensagem"
              >
                    <Send className="w-5 h-5 text-white" />
                  </button>
            ) : (
                  <button
                type="button"
                onClick={startRecording}
                    className="p-2 rounded-full hover:bg-[#e0e0e0] transition-colors"
                    title="Gravar áudio"
              >
                    <Mic className="w-5 h-5 text-gray-600" />
                  </button>
            )}
              </div>
            </div>
          </>
      ) : (
          /* Estado de Gravação */
          <div className="p-4">
            <div className="flex items-center gap-4">
              {/* Ícone de microfone animado */}
              <div className="flex-shrink-0 p-2 bg-red-100 rounded-full">
                <Mic className="w-5 h-5 text-red-600 animate-pulse" />
            </div>
            
              {/* Visualizador de ondas */}
              <div className="flex-1 flex items-center justify-center gap-1 h-8">
              {audioLevels.map((level, index) => (
                <div
                  key={index}
                    className="bg-red-500 rounded-full transition-all duration-100"
                  style={{
                    width: '3px',
                    height: `${Math.max(8, level * 32)}px`,
                  }}
                />
              ))}
            </div>
            
            {/* Timer */}
              <div className="flex-shrink-0 text-red-600 font-mono text-sm font-medium">
              {formatTime(recordingTime)}
          </div>
          
              {/* Botão Parar */}
              <button
            type="button"
            onClick={stopRecording}
                className="flex-shrink-0 p-2 rounded-full bg-red-500 hover:bg-red-600 transition-colors"
                title="Parar gravação"
          >
            <Square className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
      )}
      </div>
    </div>
  );
}
